/**
 * React Query hooks for Position Comment operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getPositionComments,
  addPositionComment,
  updatePositionComment,
  deletePositionComment,
  type PositionComment,
} from '../positionCommentApi';

/**
 * Hook to fetch all comments for a position.
 *
 * @param positionId - Position ID to fetch comments for
 * @param enabled - Whether to enable query (default: true)
 */
export function usePositionComments(positionId: number | null, enabled: boolean = true) {
  return useQuery<PositionComment[]>({
    queryKey: ['positionComments', positionId],
    queryFn: () => getPositionComments(positionId!),
    enabled: enabled && !!positionId,
    staleTime: 2 * 60 * 1000, // Consider data fresh for 2 minutes
  });
}

/**
 * Hook to create a new position comment.
 * Automatically invalidates the comments cache on success.
 */
export function useAddPositionComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      positionId,
      commentText,
      nextRevisitDate,
    }: {
      positionId: number;
      commentText: string;
      nextRevisitDate?: string | null;
    }) => addPositionComment(positionId, commentText, nextRevisitDate),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['positionComments', variables.positionId] });
      // Also invalidate activity timeline since comments appear there
      queryClient.invalidateQueries({ queryKey: ['positionThesis'] });
    },
    onError: (error: Error) => {
      console.error('Failed to add position comment:', error);
      throw error;
    },
  });
}

/**
 * Hook to update an existing position comment (in-place edit).
 * Automatically invalidates the comments cache on success.
 */
export function useUpdatePositionComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      commentId,
      commentText,
      nextRevisitDate,
    }: {
      commentId: number;
      commentText?: string;
      nextRevisitDate?: string | null;
    }) => updatePositionComment(commentId, commentText, nextRevisitDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['positionComments'] });
      // Also invalidate activity timeline
      queryClient.invalidateQueries({ queryKey: ['positionThesis'] });
    },
    onError: (error: Error) => {
      console.error('Failed to update position comment:', error);
      throw error;
    },
  });
}

/**
 * Hook to delete a position comment.
 * Automatically invalidates the comments cache on success.
 */
export function useDeletePositionComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commentId: number) => deletePositionComment(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['positionComments'] });
      // Also invalidate activity timeline
      queryClient.invalidateQueries({ queryKey: ['positionThesis'] });
    },
    onError: (error: Error) => {
      console.error('Failed to delete position comment:', error);
      throw error;
    },
  });
}
